import React, { useEffect, useMemo, useState } from "react";
import {
  DealData,
  DealQuestionnaire,
  DealsQuestionnaireData
} from "@/pages/deals/types";
import {
  useDealsQuestionnairesSettings,
  useDealsQuestionnairesResponses
} from "@/pages/deals/hooks";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { find, isEmpty, isNull, map, sortBy } from "lodash";
import WorkflowsMenu from "./components/WorkflowsMenu";
import QuestionnaireDetailPanel from "./components/QuestionnaireDetailPanel";
import { ScoreType } from "@/pages/deals/scoreConfig";
import Spinner from "@/components/ui/spinner";
import EmptyState from "./components/EmptyState";
import { cn } from "@/utils/utils";
import { getQuestionnaireIds } from "./utils";

interface Props {
  deal?: DealData;
  openQuestionnaireId?: string;
  onQuestionnaireOpened?: () => void;
}

const NewDealStageWorkflowsInternal: React.FC<Props & { deal: DealData }> = ({
  deal,
  openQuestionnaireId,
  onQuestionnaireOpened
}) => {
  const stages = useDealStages().data?.stages;
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    string | undefined
  >(undefined);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);

  const stagesIds = useMemo(() => map(stages, (stage) => stage?.id), [stages]);

  const {
    data: questionnairesSettings,
    refetch: refetchSettings,
    isLoading: isLoadingQuestionnaires
  } = useDealsQuestionnairesSettings(stagesIds, deal.id);

  const questionnaireIds = useMemo(
    () => getQuestionnaireIds(questionnairesSettings),
    [questionnairesSettings]
  );

  const {
    data: questionnairesResponses,
    refetch: refetchResponses,
    isLoading: isLoadingScore
  } = useDealsQuestionnairesResponses(deal.id, questionnaireIds);

  const handleRefetch = async () => {
    await Promise.all([refetchSettings(), refetchResponses()]);
  };

  const allQuestionnaires = useMemo(() => {
    const questionnaires: DealsQuestionnaireData[] = [];
    stagesIds.forEach((stageId) => {
      const stageQuestionnaires =
        questionnairesSettings[stageId]?.data?.questionnaires;
      if (stageQuestionnaires) {
        const sortedQuestionnaires = sortBy(
          stageQuestionnaires,
          "questionnaireOrder"
        );
        sortedQuestionnaires.forEach((questionnaire) => {
          const responses =
            questionnairesResponses[questionnaire?.id]?.data?.responses;
          const score = questionnairesResponses[questionnaire?.id]?.data?.score;
          questionnaires.push({
            ...questionnaire,
            responses,
            score
          });
        });
      }
    });
    return questionnaires;
  }, [stagesIds, questionnairesSettings, questionnairesResponses]);

  // Set default selected questionnaire to the first one in the current stage after all questionnaires are loaded
  useEffect(() => {
    if (
      !isLoadingQuestionnaires &&
      !selectedQuestionnaireId &&
      !selectedStageId &&
      !openQuestionnaireId &&
      deal.stage
    ) {
      // Find the first questionnaire in the current stage
      const currentStageQuestionnaires = allQuestionnaires.filter(
        (q) => q.stageId === deal.stage
      );
      if (currentStageQuestionnaires.length > 0) {
        setSelectedQuestionnaireId(currentStageQuestionnaires[0].id);
        setSelectedStageId(deal.stage);
      } else {
        // Current stage has no questionnaires, show empty state
        setSelectedStageId(deal.stage);
        setSelectedQuestionnaireId(undefined);
      }
    }
  }, [
    allQuestionnaires,
    selectedQuestionnaireId,
    selectedStageId,
    isLoadingQuestionnaires,
    openQuestionnaireId,
    deal.stage
  ]);

  // Handle questionnaire opened from props
  useEffect(() => {
    if (openQuestionnaireId) {
      setSelectedQuestionnaireId(openQuestionnaireId);
      onQuestionnaireOpened?.();
    }
  }, [openQuestionnaireId, onQuestionnaireOpened]);

  const selectedQuestionnaire = useMemo(() => {
    const questionnaire = allQuestionnaires.find(
      (q) => q.id === selectedQuestionnaireId
    );
    if (questionnaire && !questionnaire.isDeleted) return questionnaire;
    // Selected questionnaire was deleted — fall back to first non-deleted one in the same stage
    const activeStageQuestionnaires = allQuestionnaires.filter(
      (q) => q.stageId === selectedStageId && !q.isDeleted
    );
    return activeStageQuestionnaires[0] ?? undefined;
  }, [allQuestionnaires, selectedQuestionnaireId, selectedStageId]);

  const pocQuestionnaire = useMemo(() => {
    return find(
      allQuestionnaires,
      (questionnaire: DealsQuestionnaireData) =>
        questionnaire.kpiValue === ScoreType.POC
    );
  }, [allQuestionnaires]);

  const questionnairesRelations = useMemo(() => {
    const questionnaireMap = Object.values(questionnairesSettings).reduce(
      (map, fullResponse) => {
        fullResponse?.data?.questionnaires?.forEach((questionnaire) => {
          const responses =
            questionnairesResponses[questionnaire?.id]?.data?.responses;
          const score = questionnairesResponses[questionnaire?.id]?.data?.score;
          map[questionnaire.id] = { ...questionnaire, responses, score };
        });
        return map;
      },
      {} as Record<string, DealsQuestionnaireData>
    );
    return Object.values(questionnairesSettings).reduce(
      (acc, fullResponse) => {
        fullResponse?.data?.questionnaires?.forEach((questionnaire) => {
          if (!isNull(questionnaire.relatedQuestionnaireId)) {
            const relatedQuestionnaire =
              questionnaireMap[questionnaire.relatedQuestionnaireId];
            if (relatedQuestionnaire) {
              acc[questionnaire.id] = relatedQuestionnaire;
            }
          }
        });
        return acc;
      },
      {} as Record<string, DealQuestionnaire>
    );
  }, [questionnairesSettings, questionnairesResponses]);

  const handleQuestionnaireSelect = (questionnaireId: string) => {
    setSelectedQuestionnaireId(questionnaireId);
    const questionnaire = allQuestionnaires.find(
      (q) => q.id === questionnaireId
    );
    if (questionnaire?.stageId) {
      setSelectedStageId(questionnaire.stageId);
    }
  };

  const handleStageSelect = (stageId: number) => {
    setSelectedStageId(stageId);
    const stageQuestionnaires = allQuestionnaires.filter(
      (q) => q.stageId === stageId
    );
    if (stageQuestionnaires.length > 0) {
      setSelectedQuestionnaireId(stageQuestionnaires[0].id);
    } else {
      setSelectedQuestionnaireId(undefined);
    }
  };

  const currentStageHasNoQuestionnaires = useMemo(() => {
    if (!selectedStageId) return false;
    const stageQuestionnaires = allQuestionnaires.filter(
      (q) => q.stageId === selectedStageId && !q.isDeleted
    );
    return stageQuestionnaires.length === 0;
  }, [selectedStageId, allQuestionnaires]);

  if (!isLoadingQuestionnaires && isEmpty(questionnaireIds))
    return <EmptyState isEmptyWorkflowsMode />;

  return (
    <div className="flex gap-4 xl:gap-6 xxl:gap-10 h-[71vh] justify-center">
      <WorkflowsMenu
        dealStage={deal?.stage}
        stages={stages}
        stagesIds={stagesIds}
        questionnairesSettings={questionnairesSettings}
        questionnairesResponses={questionnairesResponses}
        selectedQuestionnaireId={selectedQuestionnaireId}
        onQuestionnaireSelect={handleQuestionnaireSelect}
        onStageSelect={handleStageSelect}
        selectedStageId={selectedStageId}
        isLoadingScore={isLoadingScore}
      />
      <div
        className={cn(
          "w-full h-full bg-surface-widget-card border border-border-border1 rounded-2xl xl:max-w-[1280px] ",
          currentStageHasNoQuestionnaires && "bg-transparent border-transparent"
        )}
      >
        {currentStageHasNoQuestionnaires ? (
          <EmptyState />
        ) : selectedQuestionnaire ? (
          <QuestionnaireDetailPanel
            dealId={deal.id}
            questionnaire={selectedQuestionnaire}
            pocQuestionnaire={pocQuestionnaire}
            relatedQuestionnaire={
              questionnairesRelations[selectedQuestionnaire.id]
            }
            handleRefetch={handleRefetch}
            isLoadingScore={isLoadingScore}
            onOpenRelatedQuestionnaire={() => {
              const relatedQuestionnaire =
                questionnairesRelations[selectedQuestionnaire.id];
              if (relatedQuestionnaire?.id) {
                setSelectedQuestionnaireId(relatedQuestionnaire.id);
              }
            }}
            onOpenPocQuestionnaire={() => {
              if (pocQuestionnaire?.id) {
                setSelectedQuestionnaireId(pocQuestionnaire.id);
              }
            }}
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}
      </div>
    </div>
  );
};

const NewDealStageWorkflows: React.FC<Props> = (props) => {
  if (!props?.deal) {
    return null;
  }

  return <NewDealStageWorkflowsInternal {...props} deal={props.deal} />;
};

export default NewDealStageWorkflows;
